import {
  compact,
  type Entries,
  flatten,
  isarray,
  valsOrNone,
} from '@quromedical/utils';
import type { Extension, FhirResource } from 'fhir/r4';
import type { ResourceType } from '../../types';
import type {
  ArrayExtension,
  ExtensionConfigs,
  ExtensionType,
  SingleExtension,
} from './config';
import { resourceExtensionConfigs } from './config';
import type { ExtensionKeys } from './keys';
import { extensionKeys } from './keys';
import type { ExtensionUrl } from './url';

export { ExtensionUrl } from './url';

/**
 * Find the an extension with the provided `url` if it exists
 * @param url URL for the extension
 * @param extensions list of extensions to search from
 */
export const findExtension = (
  url: ExtensionUrl,
  extensions: Extension[] = [],
): Extension | undefined => extensions.find((e) => e.url === url);

/**
 * Finds all extensions with the given `url`
 * @param url URL for the extension
 * @param extensions list of extensions to search from
 */
export const filterExtensions = (
  url: ExtensionUrl,
  extensions: Extension[] = [],
): Extension[] => extensions.filter((e) => e.url === url);

/**
 * Replace all extensions with the given `updateExtensionUrl` with the `newExtension`
 *
 * @param updateExtensionUrl URL of extensions to replace
 * @param existingExtensions Existing extensions from entity
 * @param newExtensions New extensions to add in place of those with the `updateExtensionUrl`
 */
export const updateExtensions = (
  updateExtensionUrl: ExtensionUrl,
  existingExtensions: Extension[] = [],
  newExtensions: Extension[] = [],
): Extension[] | undefined => {
  const retainExtensions = existingExtensions?.filter(
    (ext) => ext.url !== updateExtensionUrl,
  );

  return valsOrNone([...retainExtensions, ...newExtensions]);
};

/**
 * Get the value with the correct value type from a known extension with a specific url
 */
export const getExtensionValue =
  <TUrl extends ExtensionUrl>(url: TUrl) =>
  (extension?: Extension): Extension[ExtensionKeys[TUrl]] | undefined =>
    extension && extension[extensionKeys[url]];

/**
 * Create an extension with the correctly set value type given an extension url
 */
export const createExtensionValue =
  <TUrl extends ExtensionUrl, TBaseValue = unknown>(url: TUrl) =>
  (
    value: Exclude<Extension[ExtensionKeys[TUrl]] & TBaseValue, undefined>,
  ): Extension => ({
    url,
    [extensionKeys[url]]: value,
  });

/**
 * Find the value with the correct value type for an extension with the provided `url` if it exists
 */
export const findExtensionValue =
  <TUrl extends ExtensionUrl>(url: TUrl | Readonly<TUrl>) =>
  (
    extensions: Extension[] = [],
  ): Extension[ExtensionKeys[TUrl]] | undefined => {
    const foundExtension = extensions.find((e) => e.url === url);
    const extensionValue = getExtensionValue<TUrl>(url)(foundExtension);

    return extensionValue;
  };

/**
 * Get extension values where there may be multiple values for the same extension
 */
export const filterExtensionValues =
  <TUrl extends ExtensionUrl>(url: TUrl) =>
  (extensions: Extension[] = []): Extension[ExtensionKeys[TUrl]][] => {
    const foundExtensions = extensions
      .filter((e) => e.url === url)
      .map(getExtensionValue<TUrl>(url));

    // if for some reason the data in FHIR is not the expected type we filter it out, this may
    // happen due to drift in implementation/changes over time - we don't want to return
    // `undefined` values to the consumer
    return compact(foundExtensions);
  };

type WithExtension = {
  extension?: Extension[];
};

type OptionArray<T> = Array<T | undefined>;

/**
 * Takes an array of extensions and adds them to the resource without overwriting any existing extensions.
 * @param resource
 * @param extension
 * @returns
 */
export const addExtensionToResource = <TResource extends WithExtension>(
  resource: TResource,
  extension: Extension | OptionArray<Extension>,
): TResource => {
  const newExtensions = Array.isArray(extension) ? extension : [extension];
  const resourceExtensions = resource.extension || [];

  const combinedExtensions = [...resourceExtensions, ...newExtensions];

  return {
    ...resource,
    extension: valsOrNone(combinedExtensions),
  };
};

/**
 * Indexes to the Extension Configuration for a given resource or defaults to an empty object
 */
type ResourceExtensionConfig<TResourceType extends ResourceType> =
  TResourceType extends keyof ExtensionConfigs
    ? ExtensionConfigs[TResourceType]
    : {};

/**
 * Resolves the type an extension's value given the extension URL
 */
type ResolveSingleExtension<TUrl> = Extension[ExtensionKeys[TUrl &
  SingleExtension]];

/**
 * Resolves an array extension type using a `SingleExtension` as the basis and wrapping it as an
 * array in which all members are defined
 */
type ResolveArrayExtension<TArrayExtension extends ArrayExtension> = Array<
  Exclude<ResolveSingleExtension<TArrayExtension[0]>, undefined>
>;

/**
 * A type for resolving the value of an extension based on the extension config for a specific
 * resource. This type resolves the allowed values in the Resource config to their respective
 * Typescript type
 *
 * Addition of more complex extension configs can be done by defining them as part of
 * `ExtensionType` and adding the appropriate resolver in the list below
 */
type ExtensionValue<
  TObj extends Record<string, ExtensionType>,
  TKey extends keyof TObj,
> = TObj[TKey] extends ArrayExtension
  ? ResolveArrayExtension<TObj[TKey]>
  : ResolveSingleExtension<TObj[TKey]>;

export type ResourceExtensionValue<TResourceType extends ResourceType> = {
  [Property in keyof ResourceExtensionConfig<TResourceType>]?: ExtensionValue<
    ResourceExtensionConfig<TResourceType>,
    Property
  >;
};

/**
 * Adds some type mapping on top of the `Array.isArray` method
 */
const isMultiExtension = (data: ExtensionType): data is ArrayExtension =>
  Array.isArray(data);

const getExtensionUrl = (url: ExtensionType): ExtensionUrl => {
  if (isMultiExtension(url)) {
    return url[0];
  }

  return url as ExtensionUrl;
};

/**
 * Get resource data from extensions using the Extension Config for the resource
 */
export const mapExtensionsToData = <
  TResource extends FhirResource & WithExtension,
>(
  resource: TResource,
): ResourceExtensionValue<ResourceType<TResource>> => {
  const { extension, resourceType } = resource;
  const resourceConfig = (resourceExtensionConfigs as ExtensionConfigs)[
    resourceType
  ];

  if (!resourceConfig) {
    return {};
  }

  const entries = Object.entries(resourceConfig) as Entries<
    typeof resourceConfig
  >;

  const result = entries.reduce((prev, curr) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore FIXME: TS is not able to infer the type of `curr` correctly
    const [key, extensionConfig] = curr;

    const extensionUrl = getExtensionUrl(extensionConfig);
    const handler = isMultiExtension(extensionConfig)
      ? filterExtensionValues
      : findExtensionValue;

    return {
      ...prev,
      [key]: handler(extensionUrl)(extension),
    };
  }, {});
  return result;
};

export const createMultipleExtensions = <
  TUrl extends ExtensionUrl,
  TBaseValue = unknown,
>(
  url: ExtensionUrl,
  extensionDataArray: Exclude<
    Extension[ExtensionKeys[TUrl]] & TBaseValue,
    undefined
  >[],
): Extension[] => {
  return extensionDataArray.map((val) => createExtensionValue(url)(val));
};

export const mapDataToExtensions = <TResource extends FhirResource>(
  resource: TResource,
  extensionData: ResourceExtensionValue<ResourceType<TResource>>,
): Extension[] | undefined => {
  const resourceConfig = (resourceExtensionConfigs as ExtensionConfigs)[
    resource.resourceType
  ];

  if (!resourceConfig) {
    return undefined;
  }

  const keys = Object.keys(extensionData) as (keyof ResourceExtensionValue<
    ResourceType<TResource>
  >)[];

  const extension = keys.map((key) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore FIXME: TS is not able to infer the type of `curr` correctly
    const configUrl = resourceConfig[key as string];
    const extensionUrl = getExtensionUrl(configUrl);

    type TData = ResourceExtensionValue<TResource['resourceType']>;
    const data = extensionData[key];

    if (!data) {
      return;
    }

    const result = !isarray(data)
      ? createExtensionValue(extensionUrl)(data as TData)
      : createMultipleExtensions(
          getExtensionUrl(extensionUrl),
          data as TData[],
        );

    return result;
  });

  return compact(flatten(extension));
};
